import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  IconButton,
  CircularProgress,
  Badge
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useAuth } from '../../contexts/AuthContext';
import { db, storage } from '../../firebase/config';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { COLLECTIONS } from '../../firebase/collections';

const TechnicalServiceChat = ({ requestId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [requestDetails, setRequestDetails] = useState(null);
  const { currentUser, userService } = useAuth();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Récupérer les détails de la demande
  useEffect(() => {
    const fetchRequestDetails = async () => {
      try {
        const requestDoc = await getDoc(doc(db, COLLECTIONS.WORK_REQUESTS, requestId));
        if (requestDoc.exists()) {
          setRequestDetails({
            id: requestDoc.id,
            ...requestDoc.data()
          });
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des détails de la demande:', error);
        setError('Impossible de charger les détails de la demande');
      }
    };

    if (requestId) {
      fetchRequestDetails();
    }
  }, [requestId]);

  // Écouter les nouveaux messages
  useEffect(() => {
    if (!requestId) return;

    setLoading(true);
    const q = query(
      collection(db, COLLECTIONS.MESSAGES),
      where('requestId', '==', requestId),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const messagesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }));
      setMessages(messagesData);
      setLoading(false);
    }, (error) => {
      console.error('Erreur lors de l\'écoute des messages:', error);
      setError('Erreur lors du chargement des messages');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [requestId]);

  // Faire défiler vers le bas lorsque de nouveaux messages arrivent
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !file) || !requestId) return;

    try {
      setSending(true);
      setError('');

      let fileUrl = null;
      let fileType = null;
      let fileName = null;

      // Si un fichier est joint, le télécharger d'abord
      if (file) {
        const fileRef = ref(storage, `messages/${requestId}/${Date.now()}_${file.name}`);
        await uploadBytes(fileRef, file);
        fileUrl = await getDownloadURL(fileRef);
        fileType = file.type;
        fileName = file.name;
      }

      // Ajouter le message à Firestore
      await addDoc(collection(db, COLLECTIONS.MESSAGES), {
        requestId,
        senderId: currentUser.uid,
        senderName: userService?.name || currentUser.displayName || 'Utilisateur',
        senderRole: userService?.role || 'technical',
        content: newMessage.trim(),
        fileUrl,
        fileType,
        fileName,
        timestamp: serverTimestamp(),
        read: false
      });

      // Réinitialiser le formulaire
      setNewMessage('');
      setFile(null);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setError('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return <InsertDriveFileIcon />;
    
    if (fileType.startsWith('image/')) {
      return <ImageIcon />;
    } else if (fileType === 'application/pdf') {
      return <PictureAsPdfIcon />;
    } else {
      return <InsertDriveFileIcon />;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    return timestamp.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
      {/* En-tête */}
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6">
          Discussion - {requestDetails?.title || `Demande #${requestId.substring(0, 8)}`}
        </Typography>
        {requestDetails && (
          <Typography variant="body2">
            {requestDetails.requestingServiceName} - {requestDetails.status}
          </Typography>
        )}
      </Box>

      {/* Liste des messages */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            Aucun message. Commencez la conversation !
          </Typography>
        ) : (
          <List>
            {messages.map((message, index) => {
              const isCurrentUser = message.senderId === currentUser.uid;
              
              return (
                <React.Fragment key={message.id}>
                  <ListItem 
                    sx={{ 
                      flexDirection: isCurrentUser ? 'row-reverse' : 'row',
                      alignItems: 'flex-start',
                      mb: 1
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: isCurrentUser ? '0px' : '40px' }}>
                      {!isCurrentUser && (
                        <Avatar sx={{ bgcolor: message.senderRole === 'technical' ? 'primary.main' : 'secondary.main' }}>
                          {message.senderName?.charAt(0) || '?'}
                        </Avatar>
                      )}
                    </ListItemAvatar>
                    <Box 
                      sx={{ 
                        maxWidth: '70%',
                        bgcolor: isCurrentUser ? 'primary.light' : 'grey.100',
                        borderRadius: 2,
                        p: 1.5,
                        ml: isCurrentUser ? 1 : 0,
                        mr: isCurrentUser ? 0 : 1
                      }}
                    >
                      {!isCurrentUser && (
                        <Typography variant="subtitle2" color="text.secondary">
                          {message.senderName}
                        </Typography>
                      )}
                      
                      {message.content && (
                        <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                          {message.content}
                        </Typography>
                      )}
                      
                      {message.fileUrl && (
                        <Box sx={{ mt: message.content ? 1 : 0 }}>
                          {message.fileType?.startsWith('image/') ? (
                            <Box 
                              component="img" 
                              src={message.fileUrl} 
                              alt="Image jointe" 
                              sx={{ 
                                maxWidth: '100%', 
                                maxHeight: '200px', 
                                borderRadius: 1,
                                cursor: 'pointer'
                              }}
                              onClick={() => window.open(message.fileUrl, '_blank')}
                            />
                          ) : (
                            <Button
                              startIcon={getFileIcon(message.fileType)}
                              variant="outlined"
                              size="small"
                              href={message.fileUrl}
                              target="_blank"
                              sx={{ mt: 1 }}
                            >
                              {message.fileName || 'Fichier joint'}
                            </Button>
                          )}
                        </Box>
                      )}
                      
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          display: 'block', 
                          textAlign: 'right',
                          mt: 0.5
                        }}
                      >
                        {formatTimestamp(message.timestamp)}
                      </Typography>
                    </Box>
                  </ListItem>
                  
                  {index < messages.length - 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                      {new Date(message.timestamp).toDateString() !== 
                       new Date(messages[index + 1].timestamp).toDateString() && (
                        <Divider sx={{ width: '80%' }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(messages[index + 1].timestamp).toLocaleDateString()}
                          </Typography>
                        </Divider>
                      )}
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </List>
        )}
      </Box>

      {/* Zone de saisie */}
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
        {file && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, overflow: 'hidden' }}>
              {getFileIcon(file.type)}
              <Typography variant="body2" sx={{ ml: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setFile(null)}>
              &times;
            </IconButton>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            color="primary" 
            onClick={() => fileInputRef.current.click()}
            disabled={sending}
          >
            <AttachFileIcon />
          </IconButton>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          
          <TextField
            fullWidth
            placeholder="Écrivez votre message..."
            variant="outlined"
            size="small"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending}
            multiline
            maxRows={4}
            sx={{ mx: 1 }}
          />
          
          <Button
            variant="contained"
            color="primary"
            endIcon={<SendIcon />}
            onClick={handleSendMessage}
            disabled={(!newMessage.trim() && !file) || sending}
          >
            {sending ? <CircularProgress size={24} /> : 'Envoyer'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default TechnicalServiceChat;
